import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FolderOpen, Plus, Search, Pencil, Users as UsersIcon,
  Calendar, AlertTriangle, Briefcase, Phone,
  Lock, ArrowLeft, Clock,
} from "lucide-react";
import { useApi } from "../../../hooks/useApi";
import { useAuth } from "../../../contexts/AuthContext";
import { useDashTheme } from "../hooks/useDashTheme";
import { useBinding } from "../hooks/useBinding";
import { PageShell } from "../components/PageShell";
import { Card, Button, Badge, Avatar } from "../components/primitives";
import { DataTable, EmptyState, Pagination } from "../components/DataTable";
import { Modal, Field, Input, Textarea } from "../components/forms";
import { Drawer } from "../components/Drawer";
import { ConnectRow, DrawerSection } from "../components/ConnectRow";
import { projectColumns } from "./projects/projectColumns";
import { isGeneralProject, fmtDate } from "./projects/projectHelpers";
import { useProjectStats } from "./projects/useProjectStats";
import { ProjectGoalsPanel } from "./projects/ProjectGoalsPanel";
import { ProjectAnalyticsPanel } from "./projects/ProjectAnalyticsPanel";
import { ProjectsGrid } from "./projects/ProjectsGrid";
import { ProjectsTimeline } from "./projects/ProjectsTimeline";
import { StatusChips, ViewSwitch } from "./projects/ProjectFilters";
import { memberEntries, typeLabel, clientLabel } from "./projects/projectVisuals";
import AntDropdown from "../../../components/UI/AntDropdown";
import AntMultiSelect from "../../../components/UI/AntMultiSelect";
import AntDatePicker from "../../../components/UI/AntDatePicker";
import { isCrmEnabledForOrg } from "../../../modules/crm/utils/orgGating";
import { DASHBOARD_BASE } from "../../../config/featureFlags";
import { StealthProjectSelector } from "../components/StealthProjectSelector";

const PAGE_SIZE = 12;
const blankForm = { name: "", description: "", clientId: "", startDate: "", endDate: "", budget: "", status: "active", teamMembers: [] };

// Grid / Timeline / Table are three readings of the same page of records, so the
// choice is a preference rather than a destination — remembered locally, not put
// in the URL, and never allowed to resolve to a view that no longer exists.
const VIEW_KEY = "tg.projects.view";
const VALID_VIEWS = ["grid", "timeline", "table"];
const readStoredView = () => {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    return VALID_VIEWS.includes(v) ? v : "grid";
  } catch (_) {
    return "grid"; // private mode / storage disabled
  }
};

// Full status set (legacy parity): form offers these; the General Project is a
// protected system project that must not be renamed / deleted / archived.
const FORM_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "planning", label: "Planning" },
  { value: "on_hold", label: "On Hold" },
  { value: "completed", label: "Completed" },
];

// The list filter, in chip order. `all` is not a project status — it clears the
// status param. Labels come from projectVisuals so the chip, the card badge and
// the table cell can never disagree about what a status is called.
const LIST_STATUSES = ["active", "planning", "on_hold", "completed", "cancelled", "archived"];

export default function ManageProjectsV2() {
  const t = useDashTheme();
  const api = useApi();
  const binding = useBinding();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // CRM connections (allow + calls) are org-gated by crmEnabled flag (set by super-admin).
  // window.__orgProfile is set by parent Layout after billing/profile fetch.
  const showCrm = isCrmEnabledForOrg(window.__orgProfile);

  // Viewing another user's projects (?userId=) — legacy parity.
  const viewUserId = searchParams.get("userId");
  const [viewingUser, setViewingUser] = React.useState(null);

  // Permission gating (legacy parity).
  const hasAddProjectPermission =
    user?.role === "main_admin" || user?.role === "org_admin" ||
    (user?.role === "org_user" && user?.permissions?.addProject);
  const canEditProjects = hasAddProjectPermission;
  const canManageProjects = (!viewUserId || viewUserId === user?.id) && hasAddProjectPermission;

  // Base path of the V2 shell (flag-aware): /dashboard when NEW_UI is on, else /dashboard/v1.
  const base = DASHBOARD_BASE;

  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [pages, setPages] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [view, setView] = React.useState(readStoredView);
  const [counts, setCounts] = React.useState({ total: 0, all: 0, active: 0, planning: 0, on_hold: 0, completed: 0, cancelled: 0, archived: 0 });
  const [users, setUsers] = React.useState([]);

  // drawer / dialog state
  const [editing, setEditing] = React.useState(null);   // project | "new" | null
  const [form, setForm] = React.useState(blankForm);
  const [saving, setSaving] = React.useState(false);
  const [toDelete, setToDelete] = React.useState(null);
  const [info, setInfo] = React.useState(null);         // project | null (read-only details drawer)
  const [goalsProject, setGoalsProject] = React.useState(null);         // goals slide-in
  const [analyticsProject, setAnalyticsProject] = React.useState(null); // analytics slide-in
  // One slide-in at a time — stacking two drawers was the "overlapping" look.
  const openGoals = React.useCallback((p) => { setAnalyticsProject(null); setInfo(null); setEditing(null); setGoalsProject(p); }, []);
  const openAnalytics = React.useCallback((p) => { setGoalsProject(null); setInfo(null); setEditing(null); setAnalyticsProject(p); }, []);
  const openInfo = React.useCallback((p) => { setGoalsProject(null); setAnalyticsProject(null); setEditing(null); setInfo(p); }, []);

  // Goal counts + tracked hours for the rows on screen (one request per page).
  const visibleIds = React.useMemo(() => projects.map((p) => String(p._id || p.id)), [projects]);
  const { stats, loading: statsLoading } = useProjectStats(visibleIds);

  const fetchProjects = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (search.trim()) params.append("search", search.trim());
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (viewUserId) params.append("userId", viewUserId);
      const res = await api.get(`/api/v1/projects?${params.toString()}`);
      const d = res.data?.data || {};
      setProjects(d.projects || []);
      setPages(d.pagination?.pages || 1);
      setTotal(d.pagination?.total || 0);
    } catch (err) {
      // 403 when viewing another user's projects without access → bounce back.
      if (err.response?.status === 403 && viewUserId) {
        toast.error("You don't have access to this user's projects");
        navigate(`${base}/projects`);
      }
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [api, page, search, statusFilter, viewUserId, navigate, base]);

  const fetchCounts = React.useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: "1", limit: "1000" });
      if (viewUserId) params.append("userId", viewUserId);
      const res = await api.get(`/api/v1/projects?${params.toString()}`);
      const all = res.data?.data?.projects || [];
      const by = (s) => all.filter((p) => p.status === s).length;
      setCounts({
        total: all.length, all: all.length,
        active: by("active"), planning: by("planning"), on_hold: by("on_hold"),
        completed: by("completed"), cancelled: by("cancelled"), archived: by("archived"),
      });
    } catch (_) {}
  }, [api, viewUserId]);

  // Role-aware member fetch (legacy parity): admins / managers / addTeamMember
  // permission see all org users; everyone else sees their accessible scope.
  const fetchUsers = React.useCallback(async () => {
    const shouldShowAll = () => {
      if (user?.role === "org_admin" || user?.role === "main_admin") return true;
      if (user?.role === "org_user") {
        if (user?.permissions?.addTeamMember) return true;
        const pos = (user?.position || "").toLowerCase();
        const mgmt = ["project manager", "product manager", "ceo", "cto", "vp", "director", "manager", "scrum master", "engineering manager", "team lead", "lead"];
        return mgmt.some((m) => pos.includes(m));
      }
      return false;
    };
    try {
      const endpoint = shouldShowAll() ? "/api/v1/users/all" : "/api/v1/users";
      const res = await api.get(endpoint);
      setUsers(res.data?.data?.users || []);
    } catch (_) {
      try { const r2 = await api.get("/api/v1/users/all"); setUsers(r2.data?.data?.users || []); } catch (__) {}
    }
  }, [api, user]);

  // Fetch the user whose projects we're viewing (for the header), legacy parity.
  const fetchViewingUser = React.useCallback(async () => {
    if (!viewUserId || viewUserId === user?.id) { setViewingUser(null); return; }
    try {
      const res = await api.get(`/api/v1/users/${viewUserId}`);
      setViewingUser(res.data?.data?.user || res.data?.data || null);
    } catch (_) { setViewingUser(null); }
  }, [api, viewUserId, user?.id]);

  React.useEffect(() => { fetchProjects(); }, [fetchProjects]);
  React.useEffect(() => { fetchCounts(); fetchUsers(); fetchViewingUser(); }, [fetchCounts, fetchUsers, fetchViewingUser]);
  React.useEffect(() => { setPage(1); }, [search, statusFilter]);

  // debounce search
  const [searchInput, setSearchInput] = React.useState("");
  React.useEffect(() => { const id = setTimeout(() => setSearch(searchInput), 350); return () => clearTimeout(id); }, [searchInput]);

  const changeView = React.useCallback((v) => {
    setView(v);
    try { window.localStorage.setItem(VIEW_KEY, v); } catch (_) { /* storage disabled — the choice just won't persist */ }
  }, []);

  const openNew = () => { setGoalsProject(null); setAnalyticsProject(null); setInfo(null); setForm(blankForm); setEditing("new"); };
  const openEdit = (p) => {
    // Field mapping fixes (legacy parity): budget can come back as budgetAmount,
    // clientId can be a populated object ref.
    const clientId = typeof p.clientId === "object" ? (p.clientId?._id || p.clientId?.id || "") : (p.clientId || "");
    const budget = p.budget != null ? p.budget : (p.budgetAmount != null ? p.budgetAmount : "");
    setGoalsProject(null); setAnalyticsProject(null); setInfo(null);
    setForm({
      name: p.name || "", description: p.description || "", clientId,
      startDate: p.startDate ? p.startDate.split("T")[0] : "", endDate: p.endDate ? p.endDate.split("T")[0] : "",
      budget: budget !== "" ? String(budget) : "", status: p.status || "active",
      teamMembers: (p.teamMembers || []).map((m) => m?.userId?.id || m?.userId?._id || m?.userId || m?.id).filter(Boolean),
    });
    setEditing(p);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Project name is required"); return; }
    // General Project protection — block rename of the system project (legacy parity).
    if (editing !== "new" && isGeneralProject(editing) && form.name.toLowerCase().trim() !== "general project") {
      toast.error("General Project cannot be modified. Critical fields are protected.");
      return;
    }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description, status: form.status };
      if (form.startDate) payload.startDate = new Date(form.startDate).toISOString();
      if (form.endDate) payload.endDate = new Date(form.endDate).toISOString();
      if (form.budget && String(form.budget).trim() !== "") payload.budget = parseFloat(form.budget);
      if (form.clientId && String(form.clientId).trim() !== "") payload.clientId = form.clientId;
      // On EDIT, send teamMembers as { userId, role } objects (legacy contract);
      // on CREATE a bare id array is expected.
      if (editing === "new") {
        payload.teamMembers = form.teamMembers || [];
      } else {
        payload.teamMembers = (form.teamMembers || []).filter(Boolean).map((userId) => ({ userId, role: "contributor" }));
      }

      let savedId;
      if (editing === "new") {
        const res = await api.post("/api/v1/projects", payload);
        savedId = res.data?.data?._id || res.data?.data?.id;
        toast.success("Project created");
      } else {
        savedId = editing.id || editing._id;
        await api.put(`/api/v1/projects/${savedId}`, payload);
        toast.success("Project updated");
      }
      // Mirror members into the linked CRM project (no-op if not linked). Never blocks.
      if (savedId) binding.syncProjectMembers(savedId, form.teamMembers || []);
      setEditing(null);
      fetchProjects(); fetchCounts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const archive = async (p) => {
    // General Project cannot be archived (legacy parity).
    if (isGeneralProject(p)) {
      toast.error("General Project cannot be archived. It is required for time tracking.");
      return;
    }
    const action = p.status === "archived" ? "unarchive" : "archive";
    const msg = action === "archive"
      ? "Archive this project? It will be moved to archived projects."
      : "Unarchive this project? It will be moved back to active projects.";
    if (!window.confirm(msg)) return;
    try {
      // Send the explicit action body (legacy contract) so the backend doesn't
      // have to infer the toggle direction.
      await api.patch(`/api/v1/projects/${p.id || p._id}/archive`, { action });
      toast.success(`Project ${action}d`);
      fetchProjects(); fetchCounts();
    } catch (err) { toast.error(err.response?.data?.message || `Failed to ${action}`); }
  };

  // Delete request — guard the General Project before opening the confirm modal.
  const requestDelete = (p) => {
    if (isGeneralProject(p)) {
      toast.error("General Project cannot be deleted. It is required for time tracking.");
      return;
    }
    setToDelete(p);
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/api/v1/projects/${toDelete.id || toDelete._id}`);
      toast.success("Project deleted");
      setToDelete(null);
      fetchProjects(); fetchCounts();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to delete"); }
  };

  // Card click → project detail page (Goals + Analytics), passing project in state.
  const openDetail = (p) => navigate(`${base}/projects/${p._id || p.id}`, { state: { project: p } });

  // One empty state for all three views — a filtered-to-nothing list must not
  // read as "you have no projects", which is a different and alarming claim.
  const filtered = search || statusFilter !== "all";
  const emptyState = (
    <EmptyState
      icon={<FolderOpen size={22} />}
      title={filtered ? "No projects match" : "No projects yet"}
      description={
        filtered
          ? "Nothing matches the current search and status filter."
          : "Create your first project to get started."
      }
      action={
        filtered
          ? <Button variant="secondary" onClick={() => { setSearchInput(""); setStatusFilter("all"); }}>Clear filters</Button>
          : (canManageProjects ? <Button variant="primary" leftIcon={<Plus size={16} />} onClick={openNew}>New project</Button> : null)
      }
    />
  );

  return (
    <PageShell
      eyebrow={viewingUser ? "Workspace · Viewing" : "Workspace"}
      title={viewingUser ? `${viewingUser.firstName || ""} ${viewingUser.lastName || ""}`.trim() + "’s projects" : "Manage projects"}
      icon={<FolderOpen size={22} />}
      topRight={
        <div className="tg-filterbar">
          <label className="tg-tool tg-tool-search">
            <Search size={14} />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search projects" aria-label="Search projects" />
          </label>
          <span className="tg-tool-sep" />
          {/* The status dropdown that used to live here is now the chip row
              below — same filter, but readable at a glance and one click deep. */}
          <ViewSwitch value={view} onChange={changeView} />
        </div>
      }
      actions={
        <>
          {/* Stealth-agent users only (org toggle + agent user; backend decides both).
              Sits with the other header actions; hidden while viewing someone else's. */}
          {!viewUserId && <StealthProjectSelector compact />}
          {viewUserId && <Button variant="secondary" leftIcon={<ArrowLeft size={15} />} onClick={() => navigate(`${base}/projects`)}>Back to my projects</Button>}
          <Button variant="secondary" leftIcon={<UsersIcon size={15} />} onClick={() => navigate(`${base}/team`)}>Manage team</Button>
          {/* New project gated on permission (admins / addProject) AND not while viewing someone else's projects. */}
          {canManageProjects && <Button variant="primary" leftIcon={<Plus size={16} />} onClick={openNew}>New project</Button>}
        </>
      }
    >
      {/* Status filter — replaces the read-only count strip. Same numbers, but
          each one now narrows the list instead of only being readable. */}
      <StatusChips counts={{ ...counts, all: counts.total }} value={statusFilter} onChange={setStatusFilter} order={LIST_STATUSES} />

      {/* One page of records, three renderings. */}
      {view === "grid" && (
        <ProjectsGrid
          projects={projects} loading={loading}
          stats={stats} statsLoading={statsLoading}
          canEdit={canEditProjects} canManage={canManageProjects}
          onOpen={openDetail} onInfo={openInfo} onEdit={openEdit}
          onArchive={archive} onDelete={requestDelete}
          onGoals={openGoals} onMembers={openAnalytics}
          emptyState={<Card padding={0}>{emptyState}</Card>}
        />
      )}

      {view === "timeline" && (
        <ProjectsTimeline
          projects={projects} loading={loading} onOpen={openDetail}
          emptyState={<Card padding={0}>{emptyState}</Card>}
        />
      )}

      {view === "table" && (
        <Card padding={0}>
          <DataTable
            loading={loading}
            skeletonRows={8}
            rows={projects}
            onRowClick={openDetail}
            emptyState={emptyState}
            columns={projectColumns({
              t, stats, statsLoading,
              canEdit: canEditProjects, canManage: canManageProjects,
              onInfo: openInfo, onEdit: openEdit, onArchive: archive,
              onDelete: requestDelete, onMembers: openAnalytics,
              onGoals: openGoals,
            })}
          />
        </Card>
      )}

      {/* Pagination sits outside the switch: the page belongs to the data, not
          to whichever rendering of it happens to be on screen. */}
      {!loading && projects.length > 0 && (
        <Card padding={0} style={{ marginTop: t.s[4] }}>
          <Pagination page={page} pages={pages} total={total} pageLabel="projects" onChange={setPage} />
        </Card>
      )}

      {/* Create / Edit drawer */}
      <Drawer
        open={!!editing} onClose={() => setEditing(null)}
        title={editing === "new" ? "New project" : "Edit project"}
        subtitle={editing === "new" ? "Create a new project" : editing?.name}
        icon={<FolderOpen size={18} />} width={520}
        footer={<>
          <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving}>{saving ? "Saving…" : editing === "new" ? "Create project" : "Save changes"}</Button>
        </>}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[4] }}>
          {/* General Project is a protected system project — name locked, not deletable/archivable. */}
          {editing && editing !== "new" && isGeneralProject(editing) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: t.s[3], borderRadius: t.r.md, background: t.status.warningSoft, color: t.status.warning, fontSize: t.font.size.sm }}>
              <Lock size={14} /> This is the system General Project. Its name is protected and it can’t be deleted or archived.
            </div>
          )}
          <Field label="Project name" required>
            <Input value={form.name} disabled={editing && editing !== "new" && isGeneralProject(editing)}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Website redesign" />
          </Field>
          <Field label="Description">
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="What is this project about?" />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: t.s[4] }}>
            <Field label="Start date">
              <AntDatePicker value={form.startDate || null} onChange={(_, ds) => setForm((f) => ({ ...f, startDate: ds || "" }))} placeholder="Select start date" getPopupContainer={(node) => node.parentNode} />
            </Field>
            <Field label="End date">
              <AntDatePicker value={form.endDate || null} onChange={(_, ds) => setForm((f) => ({ ...f, endDate: ds || "" }))} placeholder="Select end date" getPopupContainer={(node) => node.parentNode} />
            </Field>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: t.s[4] }}>
            <Field label="Budget" hint="Optional"><Input type="number" value={form.budget} onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))} placeholder="0" /></Field>
            <Field label="Status">
              <AntDropdown
                value={form.status}
                options={FORM_STATUS_OPTIONS}
                onChange={(v) => setForm((f) => ({ ...f, status: v }))}
                allowClear={false}
                getPopupContainer={(node) => node.parentNode}
              />
            </Field>
          </div>
          <Field label="Team members" hint={`${form.teamMembers.length} selected`}>
            <AntMultiSelect
              value={form.teamMembers}
              options={users.map((u) => ({ value: u.userId || u.id || u._id, label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member" }))}
              onChange={(ids) => setForm((f) => ({ ...f, teamMembers: ids || [] }))}
              placeholder="Select team members"
              getPopupContainer={(node) => node.parentNode}
            />
          </Field>

          {/* CRM allow + Calls — only for existing projects AND CRM-enabled orgs (gated like V1). */}
          {showCrm && editing && editing !== "new" && (
            <div style={{ marginTop: t.s[2] }}>
              <ProjectCrmLink t={t} project={editing} />
            </div>
          )}
          {showCrm && editing === "new" && (
            <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, padding: t.s[2] }}>
              Save the project first, then open it to enable CRM access and assign call numbers.
            </div>
          )}
        </div>
      </Drawer>

      {/* Delete confirm */}
      <Modal open={!!toDelete} onClose={() => setToDelete(null)} title="Delete project" icon={<AlertTriangle size={18} />} width={440}
        footer={<>
          <Button variant="secondary" onClick={() => setToDelete(null)}>Cancel</Button>
          <Button variant="danger" onClick={doDelete}>Delete</Button>
        </>}>
        <p style={{ fontSize: t.font.size.md, color: t.c.textSecondary, lineHeight: 1.6, margin: 0 }}>
          Are you sure you want to delete <strong style={{ color: t.c.text }}>{toDelete?.name}</strong>? This action cannot be undone.
        </p>
      </Modal>

      {/* Info drawer (read-only details) */}
      <Drawer
        open={!!info} onClose={() => setInfo(null)}
        title={info?.name} subtitle="Project details" icon={<FolderOpen size={18} />} width={460}
        footer={info ? <>
          <Button variant="secondary" leftIcon={<Pencil size={14} />} onClick={() => { const p = info; setInfo(null); openEdit(p); }}>Edit</Button>
          <Button variant="primary" onClick={() => setInfo(null)}>Close</Button>
        </> : null}
      >
        {info && <ProjectInfo t={t} p={info} showCrm={showCrm} onMembers={() => { const p = info; setInfo(null); openAnalytics(p); }} />}
      </Drawer>

      {/* Goals / Analytics — one themed slide-in at a time */}
      <ProjectGoalsPanel project={goalsProject} onClose={() => setGoalsProject(null)} />
      <ProjectAnalyticsPanel project={analyticsProject} onClose={() => setAnalyticsProject(null)} />
    </PageShell>
  );
}

function ProjectInfo({ t, p, showCrm, onMembers }) {
  const tone = p.status === "active" ? "positive" : p.status === "archived" ? "neutral" : "info";
  const members = memberEntries(p);
  const type = typeLabel(p);
  const client = clientLabel(p);
  const Row = ({ label, children }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: t.s[3], marginBottom: t.s[3], borderBottom: `1px solid ${t.c.border}` }}>
      <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: t.font.size.md, color: t.c.text }}>{children}</span>
    </div>
  );
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: t.s[4] }}>
        <Badge tone={tone} dot>{p.status}</Badge>
        {p.endDate && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: t.font.size.sm, color: t.c.textTertiary }}><Calendar size={13} /> Due {fmtDate(p.endDate)}</span>}
      </div>
      <Row label="Description">{p.description || "—"}</Row>
      {type && <Row label="Type">{type}</Row>}
      {client && <Row label="Client">{client}</Row>}
      <Row label="Start date">{p.startDate ? fmtDate(p.startDate) : "—"}</Row>
      <Row label="End date">{p.endDate ? fmtDate(p.endDate) : "—"}</Row>
      {p.code && <Row label="Project code">{p.code}</Row>}
      {p.budget != null && <Row label="Budget">{p.budget}</Row>}
      <div style={{ marginTop: t.s[2] }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Team members ({members.length})</span>
          {onMembers && <Button variant="ghost" size="sm" leftIcon={<Clock size={14} />} onClick={onMembers}>Member hours</Button>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[2], marginTop: t.s[3] }}>
          {members.length === 0 ? (
            <span style={{ fontSize: t.font.size.md, color: t.c.textTertiary }}>No members assigned</span>
          ) : members.map((m) => (
            <div key={m.key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar user={m.user} name={m.name} size={30} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: t.font.size.md, color: t.c.text, fontWeight: 500 }}>{m.name}</div>
                {/* The project role (lead / developer / …) is stored on the
                    membership and was surfaced nowhere before this. */}
                <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>
                  <span style={{ textTransform: "capitalize" }}>{m.projectRole}</span>
                  {m.email ? ` · ${m.email}` : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showCrm && (
        <div style={{ marginTop: t.s[5] }}>
          <ProjectCrmLink t={t} project={p} />
        </div>
      )}
    </div>
  );
}

// CRM link connect/disconnect for ONE project. Connect mirrors this project (+ its
// members) into CRM; disconnect unlinks (CRM project stays standalone, not deleted).
function ProjectCrmLink({ t, project }) {
  const b = useBinding();
  const id = String(project._id || project.id);
  const [link, setLink] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try { const m = await b.getProjectLinks([id]); if (alive) setLink(m[id] || { linked: false }); }
      catch (_) { if (alive) setLink({ linked: false }); }
    })();
    return () => { alive = false; };
  }, [b, id]);

  const connect = async () => {
    setLoading(true);
    try {
      await b.connectProject(id, { name: project.name, description: project.description });
      const memberIds = (project.teamMembers || []).map((m) => m?.userId?.id || m?.userId || m?.id).filter(Boolean);
      b.syncProjectMembers(id, memberIds);
      setLink({ linked: true });
      toast.success("Project linked to CRM");
    } catch (e) { toast.error(e.response?.data?.message || "Failed to link"); }
    finally { setLoading(false); }
  };
  const disconnect = async () => {
    setLoading(true);
    try { await b.disconnectProject(id); setLink({ linked: false }); toast.success("Project unlinked from CRM"); }
    catch (e) { toast.error(e.response?.data?.message || "Failed to unlink"); }
    finally { setLoading(false); }
  };

  return (
    <DrawerSection title="Connections">
      <ConnectRow
        icon={<Briefcase size={16} />}
        title="CRM"
        subtitle={link?.linked ? "Synced to CRM — members mirror automatically" : "Make this project available in the CRM"}
        connected={!!link?.linked}
        loading={loading || link === null}
        onConnect={connect}
        onDisconnect={disconnect}
      />
      {/* Calls — only relevant once linked to CRM */}
      {link?.linked && <ProjectCalls t={t} project={project} />}
    </DrawerSection>
  );
}

// Per-project Plivo numbers — pick from the org pool, mark a default caller ID.
// Only shown when the project is CRM-linked (numbers live on the linked CRM project).
function ProjectCalls({ t, project }) {
  const b = useBinding();
  const id = String(project._id || project.id);
  const [orgNumbers, setOrgNumbers] = React.useState([]);
  const [assigned, setAssigned] = React.useState([]); // [{number,label,isDefault}]
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [pool, proj] = await Promise.all([b.getOrgNumbers(), b.getProjectNumbers(id)]);
        if (!alive) return;
        setOrgNumbers(pool);
        setAssigned(proj.numbers || []);
      } catch (_) {} finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [b, id]);

  const isAssigned = (num) => assigned.some((a) => a.number === num);
  const persist = async (next) => {
    setAssigned(next);
    setSaving(true);
    try { const saved = await b.setProjectNumbers(id, next); setAssigned(saved); }
    catch (e) { toast.error(e.response?.data?.message || "Failed to save numbers"); }
    finally { setSaving(false); }
  };
  const toggleNumber = (n) => {
    if (isAssigned(n.number)) persist(assigned.filter((a) => a.number !== n.number));
    else persist([...assigned, { number: n.number, label: n.label || "", isDefault: assigned.length === 0 }]);
  };
  const makeDefault = (num) => persist(assigned.map((a) => ({ ...a, isDefault: a.number === num })));

  return (
    <div style={{ padding: t.s[3], borderRadius: t.r.md, border: `1px solid ${t.c.border}`, background: t.c.surfaceMuted }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: t.s[3] }}>
        <Phone size={15} style={{ color: t.c.textTertiary }} />
        <span style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.c.text }}>Calls — Plivo numbers</span>
        {saving && <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>saving…</span>}
      </div>
      {loading ? (
        <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>Loading numbers…</div>
      ) : orgNumbers.length === 0 ? (
        <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>No org numbers yet. Add Plivo numbers in CRM settings first.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[2] }}>
          {orgNumbers.map((n, i) => {
            const on = isAssigned(n.number);
            const isDef = assigned.find((a) => a.number === n.number)?.isDefault;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: t.r.sm, background: on ? t.status.positiveSoft : t.c.surface, border: `1px solid ${t.c.border}` }}>
                <Phone size={14} style={{ color: on ? t.status.positive : t.c.textTertiary, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.md, color: t.c.text, fontWeight: 500 }}>{n.number}</div>
                  {n.label && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{n.label}</div>}
                </div>
                {on && (
                  <button onClick={() => makeDefault(n.number)} title="Set as default caller ID"
                    style={{ fontSize: t.font.size.xs, fontWeight: 600, padding: "3px 8px", borderRadius: 99, cursor: "pointer", border: `1px solid ${isDef ? "transparent" : t.c.border}`, background: isDef ? t.c.primary : "transparent", color: isDef ? "#fff" : t.c.textSecondary }}>
                    {isDef ? "Default" : "Make default"}
                  </button>
                )}
                <Button variant={on ? "secondary" : "primary"} size="sm" onClick={() => toggleNumber(n)}>{on ? "Remove" : "Assign"}</Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
